import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { IconRegistry } from '../registries/IconRegistry';
import { MediaRegistry } from '../registries/MediaRegistry';
import type { EnhancedToolResult, ImageContent, TextContent, ToolExecutionContext } from '../types';
import { convertJsonToYaml } from '../utils';
import { getSceneDescriptions } from '../video-state';

import { Tool } from './Tool';

import { getMediaCandidatesForAgent } from 'js/src/data/recommendations/recommendations-selectors';

export const getSceneScreenshotWithDescriptionSchema = z.object({
    sceneIndex: z.int().describe('The index of the scene to get a screenshot and description for.'),
});
export type GetSceneScreenshotWithDescriptionArgs = z.infer<
    typeof getSceneScreenshotWithDescriptionSchema
>;

export class GetSceneScreenshotWithDescriptionTool extends Tool<
    typeof getSceneScreenshotWithDescriptionSchema
> {
    protected override readonly schema = getSceneScreenshotWithDescriptionSchema;
    protected override readonly toolName = TOOL_NAMES.getSceneScreenshotWithDescription;
    public override readonly subagentAccess = SubagentAccess.CAPTURE;
    protected override readonly toolDescription =
        'Provides a screenshot and detailed description of the specified scene, showing the current visual state and structure of the scene. Use this tool to review any scene changes.';

    public override getToolDisplayName(): string {
        return 'Getting scene screenshot';
    }

    public get statusIcon(): string {
        return 'photo_camera';
    }

    public async execute(
        args: GetSceneScreenshotWithDescriptionArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const sceneIndex = args.sceneIndex || 0;
        const scene = this.luminary.scenes[sceneIndex];
        const hasSubscenes = scene.subscenes.length > 1;

        // Ensure all media assets from the video are in the registry
        const mediaRegistry = MediaRegistry.getInstance();
        await mediaRegistry.addAnyUnseenItemsFromVideo(this.luminary);
        const iconRegistry = IconRegistry.getInstance();
        await iconRegistry.addAnyUnseenItemsFromVideo(this.luminary);

        // Add media candidates from recommendations to registries
        const state = this._getState();
        const mediaCandidates = getMediaCandidatesForAgent(state);
        if (mediaCandidates) {
            await mediaRegistry.addCandidatesFromRecommendations(mediaCandidates);
            await iconRegistry.addCandidatesFromRecommendations(mediaCandidates);
        }

        // Generate scene description
        const sceneDescription = convertJsonToYaml({
            dimensions: `${this.luminary.referenceResolution.width}x${this.luminary.referenceResolution.height}\n`,
            ...getSceneDescriptions(scene, this.luminary, state, true, true),
        });

        const resultContent: (TextContent | ImageContent)[] = [];

        if (hasSubscenes) {
            // Scene has subscenes - take screenshots of each subscene
            resultContent.push({
                type: 'text',
                text: `Here are the screenshots for scene ${sceneIndex} (has ${scene.subscenes.length} subscenes):`,
            });

            for (let subsceneIndex = 0; subsceneIndex < scene.subscenes.length; subsceneIndex++) {
                const canvas = document.createElement('canvas');
                const { width, height } = VideoData.getResolution(
                    this.luminary.resolutionValue,
                    this.luminary.aspectRatio,
                );
                canvas.width = width;
                canvas.height = height;

                await scene.renderPreviewToCanvas(canvas, { subsceneIndex });

                // get base64 encoded image data
                const imageData = canvas.toDataURL('image/png');

                // delete the canvas to free up memory
                canvas.remove();

                resultContent.push(
                    {
                        type: 'text',
                        text: `\nSubscene ${subsceneIndex}:`,
                    },
                    {
                        type: 'image_url',
                        image_url: {
                            url: imageData,
                        },
                    },
                );
            }
        } else {
            // Scene has no subscenes - take single screenshot
            const canvas = document.createElement('canvas');
            const { width, height } = VideoData.getResolution(
                this.luminary.resolutionValue,
                this.luminary.aspectRatio,
            );
            canvas.width = width;
            canvas.height = height;

            await scene.renderPreviewToCanvas(canvas);

            // get base64 encoded image data
            const imageData = canvas.toDataURL('image/png');

            // delete the canvas to free up memory
            canvas.remove();

            resultContent.push(
                {
                    type: 'text',
                    text: `Here is the screenshot and video structure description of scene ${sceneIndex}:`,
                },
                {
                    type: 'image_url',
                    image_url: {
                        url: imageData,
                    },
                },
            );
        }

        // Add scene description at the end
        resultContent.push({
            type: 'text',
            text: `\n\nScene Description:\n${sceneDescription}`,
        });

        const subsceneInfo = hasSubscenes ? ` (${scene.subscenes.length} subscenes)` : '';
        return {
            result: `Screenshot and description captured for scene ${sceneIndex}${subsceneInfo}.`,
            resultContent,
        };
    }
}
